import React, { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { AdminPagination } from '../components/admin/AdminPagination';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { adminService } from '../services/adminService';
import { getApiErrorMessage } from '../utils/apiError';
import { formatMoney } from '../utils/formatMoney';

const PAGE_SIZE = 25;
const inputClass =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-red';
const initialFilters = {
  search: '',
  status: '',
  roomId: '',
  betType: '',
  ticketType: '',
  currentOnly: true,
  dateFrom: '',
  dateTo: '',
};

const TABS = [
  { id: 'lao', label: 'หวยลาว' },
  { id: 'lottery', label: 'Lottery' },
  { id: 'yiki', label: 'Yiki แยกห้อง' },
  { id: 'soi-dao', label: 'Soi Dao' },
];

function formatDate(value) {
  return value ? new Date(value).toLocaleString('th-TH') : '-';
}

function userLabel(row) {
  return row.username || row.walletAddress || `User #${row.userId}`;
}

function fetchBets(tab, params) {
  if (tab === 'lottery') return adminService.getLotteryBets(params);
  if (tab === 'soi-dao') return adminService.getSoiDaoBets(params);
  if (tab === 'lao') return adminService.getLaoBets(params);
  return adminService.getYikiBets(params);
}

export function AdminBetsPage() {
  const [tab, setTab] = useState('lao');
  const [draft, setDraft] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = {
      ...filters,
      currentOnly: filters.currentOnly,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo ? `${filters.dateTo}T23:59:59` : undefined,
      limit: PAGE_SIZE,
      offset,
    };
    fetchBets(tab, params)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err, 'โหลดรายการแทงไม่สำเร็จ'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, filters, offset]);

  const changeTab = (nextTab) => {
    setTab(nextTab);
    setData(null);
    setOffset(0);
    setDraft(initialFilters);
    setFilters(initialFilters);
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setOffset(0);
    setFilters({ ...draft });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>รายการผู้แทงและเลขที่แทง</CardTitle>
        <div className="mt-3 flex flex-wrap gap-2">
          {TABS.map(({ id, label }) => (
            <Button
              key={id}
              variant={tab === id ? 'primary' : 'outline'}
              size="sm"
              onClick={() => changeTab(id)}
            >
              {label}
            </Button>
          ))}
        </div>
        <form className="mt-4 grid gap-3 md:grid-cols-4 xl:grid-cols-8" onSubmit={applyFilters}>
          <input
            className={`${inputClass} md:col-span-2`}
            placeholder="ชื่อ / wallet / เลขที่แทง"
            value={draft.search}
            onChange={(e) => setDraft({ ...draft, search: e.target.value })}
          />
          <select
            className={inputClass}
            value={draft.status}
            onChange={(e) => setDraft({ ...draft, status: e.target.value })}
          >
            <option value="">ทุกสถานะ</option>
            <option value="pending">รอผล</option>
            <option value="won">ถูกรางวัล</option>
            <option value="lost">ไม่ถูกรางวัล</option>
            {tab === 'soi-dao' ? (
              <option value="settled">จบแล้ว</option>
            ) : null}
          </select>
          {tab === 'lottery' ? (
            <select
              className={inputClass}
              value={draft.ticketType}
              onChange={(e) => setDraft({ ...draft, ticketType: e.target.value })}
            >
              <option value="">ทุกชุดเลข</option>
              <option value="normal">6 หลัก</option>
              <option value="three">3 หลัก</option>
              <option value="two">2 หลัก</option>
            </select>
          ) : tab === 'yiki' || tab === 'soi-dao' ? (
            <select
              className={inputClass}
              value={draft.roomId}
              onChange={(e) => setDraft({ ...draft, roomId: e.target.value })}
            >
              <option value="">ทุกห้อง</option>
              {(data?.rooms || []).map((room) => (
                <option key={room.id} value={room.id}>
                  {room.name}
                </option>
              ))}
            </select>
          ) : null}
          {tab === 'lottery' ? (
            <select
              className={inputClass}
              value={draft.betType}
              onChange={(e) => setDraft({ ...draft, betType: e.target.value })}
            >
              <option value="">เต็งและโต๊ด</option>
              <option value="straight">เต็ง</option>
              <option value="permutation">โต๊ด</option>
            </select>
          ) : null}
          {tab === 'yiki' ? (
            <select
              className={inputClass}
              value={draft.betType}
              onChange={(e) => setDraft({ ...draft, betType: e.target.value })}
            >
              <option value="">2 และ 3 ตัว</option>
              <option value="two">2 ตัว</option>
              <option value="three">3 ตัว</option>
            </select>
          ) : null}
          {tab === 'lao' ? (
            <select
              className={inputClass}
              value={draft.betType}
              onChange={(e) => setDraft({ ...draft, betType: e.target.value })}
            >
              <option value="">ทุกประเภท</option>
              <option value="three_top">3 ตัวบน</option>
              <option value="three_tod">3 ตัวโต๊ด</option>
              <option value="two_top">2 ตัวบน</option>
              <option value="two_bottom">2 ตัวล่าง</option>
              <option value="run_top">วิ่งบน</option>
              <option value="run_bottom">วิ่งล่าง</option>
            </select>
          ) : null}
          <input
            type="date"
            className={inputClass}
            value={draft.dateFrom}
            onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value })}
          />
          <input
            type="date"
            className={inputClass}
            value={draft.dateTo}
            onChange={(e) => setDraft({ ...draft, dateTo: e.target.value })}
          />
          <label className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={draft.currentOnly}
              onChange={(e) =>
                setDraft({ ...draft, currentOnly: e.target.checked })
              }
            />
            เฉพาะรอบปัจจุบัน
          </label>
          <Button type="submit" size="sm">
            <Search className="h-4 w-4" />
            ค้นหา
          </Button>
        </form>
        {(tab === 'yiki' || tab === 'soi-dao' || tab === 'lao') &&
        data?.openRounds?.length ? (
          <p className="mt-3 text-xs text-gray-500">
            รอบเปิด:{' '}
            {data.openRounds
              .map(
                (round) =>
                  `${round.roomId || round.periodKey || 'lao'} #${round.roundNumber}`,
              )
              .join(' · ')}
          </p>
        ) : null}
        {error ? <p className="mt-3 text-sm text-brand-red">{error}</p> : null}
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <p className="mb-3 text-sm text-gray-500">พบ {data?.total || 0} รายการ</p>
        {tab === 'lottery' ? (
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b text-gray-500">
                <th className="px-2 py-2">ผู้แทง</th>
                <th className="px-2 py-2">เลข</th>
                <th className="px-2 py-2">ประเภท</th>
                <th className="px-2 py-2">เงินแทง</th>
                <th className="px-2 py-2">สถานะ / รางวัล</th>
                <th className="px-2 py-2">เวลา</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((row) => (
                <tr key={row.id} className="border-b border-gray-100">
                  <td className="max-w-[220px] break-all px-2 py-2">
                    <span className="font-semibold">{userLabel(row)}</span>
                    <br />
                    <span className="text-xs text-gray-500">#{row.userId}</span>
                  </td>
                  <td className="px-2 py-2 font-mono text-lg font-bold">
                    {row.ticketNumber}
                  </td>
                  <td className="px-2 py-2">{row.betType || row.ticketType || '-'}</td>
                  <td className="px-2 py-2">{formatMoney(row.price)}</td>
                  <td className="px-2 py-2">
                    <span className={row.status === 'won' ? 'font-semibold text-green-700' : ''}>
                      {row.status}
                    </span>
                    {row.payout > 0 ? ` · ${formatMoney(row.payout)}` : ''}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-xs text-gray-500">
                    {formatDate(row.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {tab === 'yiki' || tab === 'lao' ? (
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b text-gray-500">
                <th className="px-2 py-2">ห้อง / รอบ</th>
                <th className="px-2 py-2">ผู้แทง</th>
                <th className="px-2 py-2">เลข / ประเภท</th>
                <th className="px-2 py-2">เงินแทง</th>
                <th className="px-2 py-2">สถานะ / รางวัล</th>
                <th className="px-2 py-2">เวลา</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((row) => (
                <tr key={row.id} className="border-b border-gray-100">
                  <td className="px-2 py-2">
                    <span className="font-semibold">{row.roomId || row.periodKey}</span>
                    <br />
                    <span className="font-mono text-xs">#{row.roundNumber}</span>
                  </td>
                  <td className="max-w-[220px] break-all px-2 py-2">
                    <span className="font-semibold">{userLabel(row)}</span>
                    <br />
                    <span className="text-xs text-gray-500">#{row.userId}</span>
                  </td>
                  <td className="px-2 py-2">
                    <span className="font-mono text-lg font-bold">{row.betNumber}</span>
                    <br />
                    <span className="text-xs text-gray-500">
                      {row.betTypeLabel || row.betType}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    {formatMoney(row.amount)} × {row.multiplier}
                  </td>
                  <td className="px-2 py-2">
                    <span className={row.status === 'won' ? 'font-semibold text-green-700' : ''}>
                      {row.status}
                    </span>
                    {row.payout > 0 ? ` · ${formatMoney(row.payout)}` : ''}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-xs text-gray-500">
                    {formatDate(row.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {tab === 'soi-dao' ? (
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b text-gray-500">
                <th className="px-2 py-2">ห้อง / รอบ</th>
                <th className="px-2 py-2">ผู้เล่น</th>
                <th className="px-2 py-2">ตำแหน่งดาว</th>
                <th className="px-2 py-2">เงินซื้อ</th>
                <th className="px-2 py-2">สถานะ / รางวัล</th>
                <th className="px-2 py-2">เวลา</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((row) => {
                const stars = row.stars || row.positions || [];
                const starLabel = Array.isArray(stars)
                  ? stars
                      .map((star) =>
                        typeof star === 'string'
                          ? star
                          : star.position || star.starNumber || star.result,
                      )
                      .filter(Boolean)
                      .join(', ')
                  : row.starPositions || '-';
                return (
                  <tr key={row.id} className="border-b border-gray-100">
                    <td className="px-2 py-2">
                      <span className="font-semibold">{row.roomId}</span>
                      <br />
                      <span className="font-mono text-xs">#{row.roundNumber}</span>
                    </td>
                    <td className="max-w-[220px] break-all px-2 py-2">
                      <span className="font-semibold">{userLabel(row)}</span>
                      <br />
                      <span className="text-xs text-gray-500">#{row.userId}</span>
                    </td>
                    <td className="max-w-[240px] break-all px-2 py-2 font-mono text-sm">
                      {starLabel || '-'}
                      {row.starCount ? (
                        <span className="ml-1 text-xs text-gray-500">
                          ({row.starCount})
                        </span>
                      ) : null}
                    </td>
                    <td className="px-2 py-2">{formatMoney(row.amount)}</td>
                    <td className="px-2 py-2">
                      <span
                        className={
                          row.status === 'won' || Number(row.payout) > 0
                            ? 'font-semibold text-green-700'
                            : ''
                        }
                      >
                        {row.status}
                      </span>
                      {row.payout > 0 ? ` · ${formatMoney(row.payout)}` : ''}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-xs text-gray-500">
                      {formatDate(row.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}
        {!loading && !(data?.items || []).length ? (
          <p className="py-8 text-center text-sm text-gray-500">ไม่พบรายการแทง</p>
        ) : null}
        <AdminPagination
          offset={offset}
          limit={PAGE_SIZE}
          total={data?.total || 0}
          loading={loading}
          onOffsetChange={setOffset}
        />
      </CardContent>
    </Card>
  );
}
